// src/components/modals/DeleteTableModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { X, AlertTriangle, Loader2, Hash, MapPin, Users } from 'lucide-react'
import TableService from '../../services/tableService'

const DeleteTableModal = ({ isOpen, onClose, onTableDeleted, table }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Helper function to safely get branch name
  const getBranchName = (table) => {
    if (!table) return 'Unknown'
    
    // Check if branchDetails exists and has name
    if (table.branchDetails?.name) {
      return table.branchDetails.name
    }
    
    // Check if branch is an object with name
    if (typeof table.branch === 'object' && table.branch?.name) {
      return table.branch.name
    }
    
    // Check if branch is a string
    if (typeof table.branch === 'string') {
      return table.branch
    }
    
    return 'Unknown'
  }

  // Handle delete confirmation
  const handleDelete = async () => {
    if (!table?._id) {
      console.error('❌ No table ID provided for deletion')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      // Enhanced debug logging
      console.log('🗑️ Deleting table:', {
        tableId: table._id,
        tableNumber: table.number,
        tableBranch: getBranchName(table),
        token: localStorage.getItem('tezlaa-access-token') ? 'Present' : 'Missing'
      })
      
      const response = await TableService.deleteTable(table._id)
      
      if (response.success) {
        console.log('✅ Table deleted successfully:', response)
        
        // Notify parent component
        onTableDeleted(table._id)
        
        // Close modal
        onClose()
      } else {
        console.log('⚠️ Delete failed with response:', response)
        setError(response.message || 'Failed to delete table')
      }
    } catch (error) {
      console.error('❌ Error deleting table:', error)
      
      // Enhanced error handling
      let errorMessage = 'Failed to delete table. Please try again.'
      
      if (error.message.includes('Not found')) {
        errorMessage = 'Table not found. It may have already been deleted.'
      } else if (error.message.includes('Unauthorized')) {
        errorMessage = 'You are not authorized to delete this table. Please login again.'
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (error.message.includes('conflict') || error.message.includes('in use')) {
        errorMessage = 'Cannot delete table. It may be currently reserved or in use.'
      } else if (error.message.includes('reserved')) {
        errorMessage = 'Cannot delete table. Please set availability to "Available" first.'
      } else if (error.message.includes('orders exist')) {
        errorMessage = 'Cannot delete table. Please complete or cancel any existing orders first.'
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Reset error when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null)
    }
  }, [isOpen])

  // Don't render if not open
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Delete Table</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Confirmation Message */}
          <div className="mb-4">
            <p className="text-gray-700 leading-relaxed">
              Are you sure you want to delete this table? This action cannot be undone.
            </p>
          </div>

          {/* Table Details */}
          {table && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-gray-900 mb-3">Table Details</h4>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <Hash className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-gray-600">Number:</span>
                  <span className="font-medium text-gray-900 ml-2">{table.number}</span>
                </div>
                
                <div className="flex items-center text-sm">
                  <MapPin className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-gray-600">Branch:</span>
                  <span className="font-medium text-gray-900 ml-2">
                    {getBranchName(table)}
                  </span>
                </div>
                
                <div className="flex items-center text-sm">
                  <Users className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-gray-600">Capacity:</span>
                  <span className="font-medium text-gray-900 ml-2">
                    {table.capacity} {table.capacity === 1 ? 'person' : 'people'}
                  </span>
                </div>
                
                <div className="flex items-center text-sm">
                  <div className="w-4 h-4 mr-2 flex-shrink-0 flex items-center justify-center">
                    {table.availability === 'available' ? '✅' : '🔒'}
                  </div>
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium ml-2 px-2 py-1 rounded-full text-xs ${
                    table.availability === 'available' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {table.availability === 'available' ? 'Available' : 'Reserved'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Warning Messages */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex items-start">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Important:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>This will permanently remove the table from the system</li>
                  <li>Any future reservations for this table will be affected</li>
                  <li>Historical data related to this table will be preserved</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={loading}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white focus:ring-2 focus:ring-red-500"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 mr-2" />
                Delete Table
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DeleteTableModal